import L from "leaflet";

/** Tile file extension from slide File row (upload-photo uses .jpg). */
export function tileExtensionFromSlideFile(file) {
  if (!file) return "png";
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  if (/\.(jpe?g)$/.test(name) || type.includes("jpeg")) return "jpg";
  if (/\.webp$/.test(name) || type.includes("webp")) return "webp";
  if (/\.png$/.test(name) || type.includes("png")) return "png";
  if (name.startsWith("upload-photo")) return "jpg";
  return "png";
}

/** Leaflet tile URL pattern from slide File row. */
export function tileUrlPatternFromSlideFile(file) {
  if (!file?.url) return "";
  const base = String(file.url).replace(/\/+$/, "");
  const ext = tileExtensionFromSlideFile(file);
  return `${base}/{z}/{x}/{y}.${ext}`;
}

export function tileUrlPatternFromRoot(tileRootUrl, tileExt = "png") {
  const base = String(tileRootUrl || "").replace(/\/+$/, "");
  const ext = String(tileExt || "png").replace(/^\./, "");
  return `${base}/{z}/{x}/{y}.${ext}`;
}

/** S3 prefix that holds `{z}/{x}/{y}` plus `tiles.json`. */
export function slideOutputPrefixFromTileUrl(tileURL) {
  return String(tileURL || "")
    .replace(/\/\{z\}\/\{x\}\/\{y\}\.[^/?#]+.*$/i, "")
    .replace(/\/+$/, "");
}

export function tileUrlWithExtension(tileURL, tileExt) {
  if (!tileURL || !tileExt) return tileURL;
  const ext = String(tileExt).replace(/^\./, "");
  return String(tileURL).replace(/(\{y\})\.[^/?#]+/i, `$1.${ext}`);
}

/**
 * Pyramid limits for the Leaflet viewer.
 * Missing manifest: use the slide's stored max_zoom, and allow two extra
 * zoom steps so Leaflet enlarges the deepest real tiles.
 */
export function slideTileLayerOptions(manifest, fallbackMaxZoom) {
  const fallback =
    typeof fallbackMaxZoom === "number" && fallbackMaxZoom >= 0 ? fallbackMaxZoom : null;
  const num = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

  if (!manifest) {
    if (fallback == null) return null;
    return {
      minZoom: 0,
      maxNativeZoom: fallback,
      maxZoom: fallback + 2,
      tileSize: 256,
      tileExt: null,
    };
  }

  const minZoom = num(manifest.min_zoom) ?? 0;
  const maxNativeZoom = num(manifest.max_native_zoom) ?? fallback;
  const maxZoom =
    num(manifest.viewer_max_zoom) ?? (maxNativeZoom != null ? maxNativeZoom + 2 : null);
  return {
    minZoom,
    maxNativeZoom,
    maxZoom,
    tileSize: num(manifest.tile_size) ?? 256,
    tileExt: manifest.tile_ext ? String(manifest.tile_ext).replace(/^\./, "") : null,
  };
}

/** Fetch `{output_prefix}/tiles.json`. Older slides have no manifest. */
export async function fetchSlideTileManifest(tileURL) {
  const prefix = slideOutputPrefixFromTileUrl(tileURL);
  if (!prefix) return null;
  try {
    const response = await fetch(`${prefix}/tiles.json`, { cache: "no-store" });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    return data;
  } catch (_) {
    return null;
  }
}

const TILE_EXTS = ["png", "jpg", "jpeg", "webp"];

function probeTile(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.referrerPolicy = "no-referrer";
    img.src = url;
  });
}

/** First extension (preferred first) that serves `{prefix}/{z}/{x}/{y}.{ext}`. */
async function findTileExt(prefix, z, x, y, preferredExt) {
  const exts = [...new Set([preferredExt, ...TILE_EXTS].filter(Boolean))];
  for (const ext of exts) {
    if (await probeTile(`${prefix}/${z}/${x}/${y}.${ext}`)) return ext;
  }
  return null;
}

/**
 * Check tiles.json against the tiles that actually exist on S3.
 * Some older slides have a manifest whose extension, zoom range or size
 * doesn't match the pyramid; trusting it leaves the web viewer blank
 * while the mobile app (which ignores tiles.json) still shows the slide.
 *
 * Returns `{ manifest, tileExt }`: `manifest` is null when it can't be
 * trusted (caller then falls back to the slide's max_zoom, like mobile),
 * `tileExt` is the extension real tiles were found with (or null).
 */
export async function verifySlideTileSource(tileURL, manifest) {
  const prefix = slideOutputPrefixFromTileUrl(tileURL);
  if (!prefix) return { manifest: null, tileExt: null };
  const urlExt = (String(tileURL).match(/\{y\}\.([^/?#]+)/i) || [])[1] || null;

  if (manifest) {
    const options = slideTileLayerOptions(manifest, null);
    const ext = options?.tileExt || urlExt;
    const minZoom = options?.minZoom ?? 0;
    const foundExt = await findTileExt(prefix, minZoom, 0, 0, ext);
    const size = imagePixelSizeAtNativeZoom(manifest);
    let deepOk = true;
    if (foundExt && size) {
      const lastX = Math.max(0, Math.floor((size.width - 1) / 256));
      const lastY = Math.max(0, Math.floor((size.height - 1) / 256));
      deepOk = await probeTile(`${prefix}/${size.nativeZoom}/${lastX}/${lastY}.${foundExt}`);
    }
    if (foundExt && deepOk) {
      return { manifest: { ...manifest, tile_ext: foundExt }, tileExt: foundExt };
    }
    console.warn("Slide tiles.json does not match the tiles on S3; ignoring it.", prefix);
  }

  // No usable manifest: detect the extension from the shallowest real tile.
  for (const z of [0, 1, 2, 3]) {
    const ext = await findTileExt(prefix, z, 0, 0, urlExt);
    if (ext) return { manifest: null, tileExt: ext };
  }
  return { manifest: null, tileExt: null };
}

/**
 * Pixel size of the image at max_native_zoom from tiles.json.
 * When max_native_zoom is above source_max_zoom, the pyramid was
 * extended and the pixel grid is larger than the source image.
 */
export function imagePixelSizeAtNativeZoom(manifest) {
  if (!manifest) return null;
  const width = Number(manifest.width);
  const height = Number(manifest.height);
  const sourceMaxZoom = Number(manifest.source_max_zoom);
  const nativeZoom = Number(manifest.max_native_zoom);
  if (![width, height, nativeZoom].every((n) => Number.isFinite(n))) return null;
  const sourceZoom = Number.isFinite(sourceMaxZoom) ? sourceMaxZoom : nativeZoom;
  const scale = 2 ** (sourceZoom - nativeZoom);
  if (!Number.isFinite(scale) || scale === 0) return null;
  return {
    width: Math.ceil(width / scale),
    height: Math.ceil(height / scale),
    nativeZoom,
  };
}

/**
 * LatLng bounds covering the slide image at its native tile zoom.
 * Works with the default Leaflet CRS (same unproject pattern used for CRS.Simple).
 */
export function slideImageLatLngBounds(map, manifest) {
  if (!map || !manifest) return null;
  const size = imagePixelSizeAtNativeZoom(manifest);
  if (!size) return null;
  const southWest = map.unproject([0, size.height], size.nativeZoom);
  const northEast = map.unproject([size.width, 0], size.nativeZoom);
  const bounds = L.latLngBounds(southWest, northEast);
  return bounds.isValid() ? bounds : null;
}

/** @deprecated use slideImageLatLngBounds */
export function simpleCrsImageBounds(map, manifest) {
  return slideImageLatLngBounds(map, manifest);
}

/**
 * Highest contiguous zoom that has tile `0/0/0` under the pyramid root.
 * Used when tiles.json is missing so Leaflet is not configured with the
 * DB default max_zoom=10 (which 404s for shallow photo pyramids).
 */
export async function probeMaxNativeZoom(tileURL, tileExt, maxProbe = 12) {
  const prefix = slideOutputPrefixFromTileUrl(tileURL);
  const ext = String(tileExt || "jpg").replace(/^\./, "");
  if (!prefix || !ext) return null;
  let highest = -1;
  for (let z = 0; z <= maxProbe; z += 1) {
    // eslint-disable-next-line no-await-in-loop
    if (await probeTile(`${prefix}/${z}/0/0.${ext}`)) highest = z;
    else break;
  }
  return highest >= 0 ? highest : null;
}

/** First `_master/...` URL that loads for an upload-photo / stitch file. */
export async function resolveSlideMasterPreviewUrl(file) {
  const candidates = slideThumbnailCandidatesFromFile(file).filter((url) =>
    /\/_master\//i.test(url)
  );
  for (const url of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await probeTile(url);
    if (ok) return url;
  }
  return null;
}

/**
 * Whether an upload-photo pyramid is finished enough for the Leaflet viewer.
 * Prefers tiles.json; falls back to a probed native zoom when the manifest
 * is absent (legacy slides).
 */
export async function assessUploadPhotoPyramid(tileURL, slideMaxZoom) {
  const rawManifest = await fetchSlideTileManifest(tileURL);
  const verified = await verifySlideTileSource(tileURL, rawManifest);
  if (verified.manifest) {
    return {
      ready: true,
      building: false,
      manifest: verified.manifest,
      tileExt: verified.tileExt,
      maxNativeZoom:
        slideTileLayerOptions(verified.manifest, slideMaxZoom)?.maxNativeZoom ?? null,
    };
  }
  if (!verified.tileExt) {
    return {
      ready: false,
      building: true,
      manifest: null,
      tileExt: null,
      maxNativeZoom: null,
    };
  }
  const probed = await probeMaxNativeZoom(tileURL, verified.tileExt);
  // Manifest missing: still building if we only have overview tiles while
  // the stored max_zoom expects a deeper pyramid. Otherwise treat as a
  // legacy complete slide and use the probed depth.
  const expected =
    typeof slideMaxZoom === "number" && slideMaxZoom >= 0 ? slideMaxZoom : null;
  const building =
    !rawManifest &&
    probed != null &&
    expected != null &&
    expected > probed;
  return {
    ready: !building && probed != null,
    building: Boolean(building) || (!rawManifest && probed == null),
    manifest: null,
    tileExt: verified.tileExt,
    maxNativeZoom: probed,
  };
}

export function isUploadPhotoSlide(slideOrGeoJson) {
  const file = slideFileFromSlide(slideOrGeoJson) || slideOrGeoJson?.Files?.[0];
  const name = String(file?.name || file?.alias || "").toLowerCase();
  return (
    name.startsWith("upload-photo") ||
    name.startsWith("capture-grid-stitch") ||
    name.startsWith("capture-stitch")
  );
}

/**
 * Initial Leaflet view for an upload-photo slide.
 *
 * Goals:
 *  • Open zoomed OUT enough that the whole slide is visible.
 *    Previously we opened at maxNativeZoom-1, which on a deep
 *    pyramid (e.g. maxZoom=8) snapped the user into a corner of
 *    the image instead of showing them the whole thing.
 *  • Centre the view on the actual image content (top-left tile
 *    pyramid corner), not on Leaflet's default geo anchor.
 */
export function uploadPhotoLeafletView(map, maxNativeZoom) {
  let z = Math.max(0, Number(maxNativeZoom));
  if (!Number.isFinite(z)) z = 4;
  /*
    Compute the centre at the slide's native zoom so the unprojection
    is anchored at the same tile across pyramids. Pixel (128, 128) is
    the centre of tile (0, 0) — the top-left tile that always exists
    in our generated pyramid.
  */
  const projectionZoom = z > 0 ? z : 1;
  const center = map.unproject(L.point(128, 128), projectionZoom);
  /*
    Open 1 level in from fully zoomed-out so the slide is visible
    without being postage-stamp-sized, but still small enough that
    the whole image fits on screen. Capped at the pyramid's native
    depth for very shallow slides.
  */
  const viewZoom = Math.min(1, z);
  return {
    center: [center.lat, center.lng],
    zoom: viewZoom,
  };
}

export function slideFileFromSlide(slide) {
  const files = slide?.Files ?? slide?.files ?? [];
  return Array.isArray(files) && files.length ? files[0] : null;
}

function uniqueUrls(urls) {
  const seen = new Set();
  const out = [];
  urls.forEach((url) => {
    const value = String(url || "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

function sessionIdFromSlideFile(file) {
  const name = String(file?.name || file?.alias || "");
  const match = name.match(
    /(?:upload-photo|capture-grid-stitch)-([0-9a-f-]{8,})/i
  );
  return match?.[1] || null;
}

/**
 * Card preview URLs, ordered from most likely to load.
 * Tries the full-resolution master image first, then overview tiles
 * with jpg/png/webp — stitch jobs write jpg, older ZIPs often used png.
 */
export function slideThumbnailCandidatesFromFile(file, preferredUrl = null) {
  if (!file?.url && !preferredUrl) return preferredUrl ? [preferredUrl] : [];
  const base = file?.url ? String(file.url).replace(/\/+$/, "") : "";
  const preferredExt = tileExtensionFromSlideFile(file);
  const exts = uniqueUrls([preferredExt, "jpg", "jpeg", "png", "webp"]);
  const sessionId = sessionIdFromSlideFile(file);
  const urls = [];

  if (preferredUrl) urls.push(preferredUrl);

  if (base) {
    if (sessionId) {
      exts.forEach((ext) => urls.push(`${base}/_master/${sessionId}.${ext}`));
    }
    exts.forEach((ext) => urls.push(`${base}/_master/latest.${ext}`));

    // Same mid-zoom tile as the mobile app (`3/3/3`), then shallower fallbacks.
    const tilePaths = ["3/3/3", "2/1/1", "2/0/0", "1/0/0", "0/0/0"];
    tilePaths.forEach((path) => {
      exts.forEach((ext) => urls.push(`${base}/${path}.${ext}`));
    });
  }

  return uniqueUrls(urls);
}

/** Library / title grid cover image — matches app `slideThumbnailUrlFromSlide`. */
export function slideThumbnailUrlFromSlide(slide) {
  const file = slideFileFromSlide(slide);
  if (!file?.url) return null;
  const base = String(file.url).replace(/\/+$/, "");
  const ext = tileExtensionFromSlideFile(file);
  return `${base}/3/3/3.${ext}`;
}

/** Swap a broken card preview to the next tile that may exist. */
export function advanceSlideThumbnailOnError(event, candidates) {
  const img = event?.currentTarget;
  if (!img || !Array.isArray(candidates) || candidates.length < 2) {
    if (img) img.style.visibility = "hidden";
    return;
  }
  const previous = Number(img.dataset.thumbIndex || "0");
  const nextIndex = Number.isFinite(previous) ? previous + 1 : 1;
  if (nextIndex >= candidates.length) {
    img.style.visibility = "hidden";
    return;
  }
  img.dataset.thumbIndex = String(nextIndex);
  img.src = candidates[nextIndex];
}
